import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { LoggerService } from '../logger/logger.service';
import { HttpHeaders } from '@angular/common/http';
import { CognitoUserPool, AuthenticationDetails, CognitoUser, CognitoUserSession, CognitoRefreshToken } from 'amazon-cognito-identity-js';

const log = new LoggerService('AUTHENTICATION SERVICE');

@Injectable({ providedIn: 'root' })
export class AuthenticationService {

  constructor() { }

  public navigationTreeGenerated = false;
  public currentSession: CognitoUserSession = null;
  public currentUser: CognitoUser = null; 
  public userData = {};

  /**
   * @description Adquiere la 'user pool' del servicio cognito utilizando las variables de entorno de cognito.
   * @returns AmazonCognitoIdentity.CognitoUserPool
   */
  private getCognitoUserPool(): CognitoUserPool {
    let cognitoUserPool = new CognitoUserPool({
      UserPoolId: environment['COGNITO_USERPOOL_ID'],
      ClientId: environment['COGNITO_APPCLIENT_ID'],
    });
    return cognitoUserPool;
  };

  /**
   * @description Inicia sesión de un usuario cognito para la pool y el cliente especificado en las variables de entorno.
   * @returns string -> loginSuccess | loginError | newPasswordRequired.
   */
  public logIn(username: string, password: string): Promise<string> {
    //log.info('[logIn] Executed.')

    const authenticationDetails = new AuthenticationDetails({ Username: username, Password: password });
    const userPool = this.getCognitoUserPool();
    const userData = { Username: username, Pool: userPool };
    const cognitoUser = new CognitoUser(userData);

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: ((session: CognitoUserSession) => {
          //log.debug('Login success, user session: ', session);
          //this.setUserData(cognitoUser, session);
          resolve('loginSuccess');
        }),
        onFailure: ((err: Error) => {
          //log.debug('Login error: ', err.message);

          let newErr = new Error('Algo salió mal al iniciar sesión.');

          if (err.message === 'User does not exist.')
            newErr = new Error('El usuario ingresado no existe.');
          else if (err.message === 'Incorrect username or password.')
            newErr = new Error('Contraseña o usuario incorrectos.');

          reject(newErr);
        }),
        newPasswordRequired: ((userAttributes, requiredAtr) => {
          //log.debug('New password and attributes required: ', requiredAtr);
          resolve('newPasswordRequired')
        })
      });
    });
  }

  /**
   * @description Vrifica si el usuario tiene una sesión de cognito iniciada.
   * @returns true si el cliente contiene una sessión correcta del usuario.
   */
  public isLoggedIn(): Promise<boolean> {

    return new Promise( (resolve, reject) =>{
      //log.debug('[isLoggedIn] ...')
      let isAuth = false;
      const userPool = this.getCognitoUserPool();
      const cognitoUser = userPool.getCurrentUser();
      //log.debug('[isLoggedIn] user pool:', userPool);
      
      //log.debug('[isLoggedIn] cognito user.', cognitoUser);
  
      if (cognitoUser != null) {
        //log.debug('[isLoggedIn]  Cognito user is not null')
  
        cognitoUser.getSession((err: Error, session: CognitoUserSession) => {
          //log.debug('[isLoggedIn]  Cognito get session called')
          if (err) {
            //log.debug('[isLoggedIn]  Error: ', err)
            //log.error('[isLoggedIn] No se encontró una sesión válida. ', err.message);
            //log.debug('[isLoggedIn] isAuth?: ', isAuth);
            reject(isAuth);
          }
          //log.debug('[isLoggedIn] User session:', session)
          this.setUserData(cognitoUser, session)
          isAuth = session.isValid();
  
          this.currentSession = session;
          this.currentUser = cognitoUser;
  
          
          //log.info('[isLoggedIn] current user: ', this.currentUser);
          //log.info('[isLoggedIn] current session: ', this.currentSession);
          //log.debug('[isLoggedIn] isAuth?: ', isAuth);

          resolve(isAuth); 
        });
      } else {
        resolve(isAuth);
      }
    });

  }
  
  /**
   * @description Inicia sesión de un usuario cognito para la pool y el cliente especificado en las variables de entorno.
   * @returns string -> confirmPasswordSuccess | confirmPasswordError.
   */
  public confirmPassword(username: string, password: string, newPassword: string, name: string): Promise<string> {
    //log.info('[confirmPassword] Executed.')
    const authenticationDetails = new AuthenticationDetails({ Username: username, Password: password });
    const userPool = this.getCognitoUserPool();
    const userData = { Username: username, Pool: userPool };
    const cognitoUser = new CognitoUser(userData);

    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: ((session: CognitoUserSession) => {
          //log.debug('[confirmPassword] Login success, user session: ', session);
          resolve('loginSuccess');
        }),
        onFailure: ((err: Error) => {
          //log.debug('Login error: ', err.message);
          let newErr = new Error('Algo salió mal al iniciar sesión.');

          if (err.message === 'User does not exist.')
            newErr = new Error('El usuario ingresado no existe.');
          else if (err.message === 'Incorrect username or password.')
            newErr = new Error('Contraseña o usuario incorrectos.');

          reject(newErr);
        }),
        newPasswordRequired: ((userAttributes, requiredAtr) => {
          //log.debug('[confirmPassword] New password and attributes required: ', requiredAtr);
          cognitoUser.completeNewPasswordChallenge(newPassword, { name: name }, {
            onSuccess: async (session) => {
              //log.debug('[confirmPassword] Confirm password success, user session: ', session);
              const idToken = session.getIdToken().getJwtToken();
              const userId =  session.getIdToken().payload['cognito:username'];

              const fetchResponse = await fetch(`${environment.EPIONE_API}/epione-core-api/users`, {
                method: 'PATCH',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({
                  'id': userId,
                  'name': name
                })
              });
              const apiResponse: Utils.ApiResponse<Entities.Organization.OrganizationData> = await fetchResponse.json();
              log.debug('API response password confirmation and name changed.', apiResponse);
              resolve('confirmPasswordSuccess');
            },
            onFailure: (err) => {
              //log.debug('[confirmPassword] Confirm password error: ', err.message);
              
              let newErr = new Error('Algo salió mal al confirmar los datos.');

              if (err.message === 'Password does not conform to policy: Password not long enough')
                newErr = new Error('La contraseña no es válida. La contraseña debe ser más larga.');
              else if (err.message === 'Password does not conform to policy: Password must have uppercase characters')
                newErr = new Error('La contraseña debe contener mayúsculas.');
              else if (err.message === 'Password does not conform to policy: Password must have numeric characters')
                newErr = new Error('La contraseña debe contener números.');
              else if (err.message === 'Password does not conform to policy: Password must have symbol characters')
                newErr = new Error('La contraseña debe contener símbolos.');
              reject(newErr)
            }
          });
        })
      });
    });
  }

  /**
   * @description Función utilizada para mantener activo el token del usuario, se llama a si misma al transcurrir 4 minutos.
   */
  public refreshSession(): void {
    //log.info('[refreshSession] ...')
    if (!this.currentSession || ! this.currentUser) return;
    //log.info('[refreshSession] current user: ', this.currentUser);
    //log.info('[refreshSession] current session: ', this.currentSession);

    this.currentUser.getUserData((err, userData) => {
      //log.debug('[refreshSession] Cognito user: ', userData);
    })
    const refreshToken = new CognitoRefreshToken({ RefreshToken: this.currentSession.getRefreshToken().getToken() });
    this.currentUser.refreshSession(refreshToken, (err: Error, newSession: CognitoUserSession) => {
      if (err) {
        //log.debug('[refreshSession] Error refresacando la sesión del ususario.');
        return;
      }

      //log.debug('oldSession: ', this.currentSession);
      this.currentSession = newSession;
      //log.debug('[refreshSession] newsession: ', this.currentSession);

      this.setUserData(this.currentUser, newSession);

      setTimeout(() => {
        this.refreshSession();
      },240000)
    });

  }

  /**
   * @description Función utilizada para declarar el atributo público userData de la clase. Se manda a llamar al hacer la prueba de loggedIn, funcion utilizada en el authentication guard.
  */
  private setUserData(cognitoUser: CognitoUser, session: CognitoUserSession): void {
    //log.debug('[setUserData] ...')
    //log.debug('[setUserData] User session: ', session.getAccessToken());

    localStorage.setItem('accessToken', session.getAccessToken().getJwtToken())
    localStorage.setItem('idToken', session.getIdToken().getJwtToken())
    localStorage.setItem('userId', session.getIdToken().payload['cognito:username']);
    //log.debug('Session id token payload:', session.getIdToken().payload)

    cognitoUser.getUserData((err, userData) => {
      //log.debug('User data: ', userData);

      for (let i = 0; i < userData.UserAttributes.length; i++) {
        const userAttr = userData.UserAttributes[i];
        this.userData[userAttr.Name] = userAttr.Value;
        
      }
    });
  }

  /**
   * @description Elimina la sesión del usuario del navegador.
   */
  public logOut(): Promise<boolean> {
    const userPool = this.getCognitoUserPool()
    const cognitoUser = userPool.getCurrentUser();
    return new Promise((resolve) => {
      cognitoUser.signOut(() => {
        //log.debug('User signed out.');
        localStorage.clear();
        resolve(true);
      });
    });
  }

  public async resetPassword(email:string, name:string) {
    const response = await fetch(`${environment.EPIONE_API}/epione-core-api/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // 'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: JSON.stringify({
        name:name,
        email:email
      })
    });

    

    //log.debug('Reset password response: ', response);
    return await response.json();
  } 

  private generateHttpHeaders(idToken: string) {
    return {
      headers: new HttpHeaders({
        //'Content-Type':  'application/json',
        //'Access-Control-Request-Headers': 'Allow-Origin',
        Authorization: `${idToken}`,
      }),
    };
  }
}
